import { EyeIcon, LinkIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { DataTableColumn } from '@odo/shared-ui';
import { RowMenu, type RowMenuItem } from '../RowMenu';
import { StatusBadge } from '../StatusBadge';
import type { OneTimeDiscountListItem } from '../../../services/billing';
import { formatMoneyDollars } from '../../../utils/money';

export type OneTimeDiscountsSortBy = 'expiry' | 'date' | 'patient';

function shortRef(id: string): string {
    return id.slice(0, 8).toUpperCase();
}

function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

function menuItemsFor(
    discount: OneTimeDiscountListItem,
    canEdit: boolean,
    onLink: (discount: OneTimeDiscountListItem) => void,
    onDelete: (discount: OneTimeDiscountListItem) => void,
    onOpenCharge: (chargeId: string) => void,
    onUnlink: (discount: OneTimeDiscountListItem) => void
): RowMenuItem[] {
    const items: RowMenuItem[] = [];
    if (canEdit && (discount.status === 'available' || discount.status === 'expiring')) {
        items.push({
            label: 'Link to charge',
            icon: <LinkIcon className="h-4 w-4 text-gray-400" />,
            onClick: () => onLink(discount)
        });
        items.push({
            label: 'Delete one-time discount',
            icon: <TrashIcon className="h-4 w-4" />,
            tone: 'danger',
            onClick: () => onDelete(discount)
        });
    }
    if (discount.status === 'applied' && discount.chargeId) {
        const chargeId = discount.chargeId;
        items.push({
            label: 'View charge',
            icon: <EyeIcon className="h-4 w-4 text-gray-400" />,
            onClick: () => onOpenCharge(chargeId)
        });
        // Unlink while the charge is pending, or canceled to free credits stuck on
        // charges canceled before cancellation auto-unlinked them.
        if (
            canEdit &&
            (discount.chargeStatus === 'pending' || discount.chargeStatus === 'canceled')
        ) {
            items.push({
                label: 'Unlink',
                icon: <XMarkIcon className="h-4 w-4 text-gray-400" />,
                onClick: () => onUnlink(discount)
            });
        }
    }
    return items;
}

interface BuildOneTimeDiscountColumnsArgs {
    canEdit: boolean;
    onLink: (discount: OneTimeDiscountListItem) => void;
    onDelete: (discount: OneTimeDiscountListItem) => void;
    onOpenCharge: (chargeId: string) => void;
    onUnlink: (discount: OneTimeDiscountListItem) => void;
}

export function buildOneTimeDiscountColumns({
    canEdit,
    onLink,
    onDelete,
    onOpenCharge,
    onUnlink
}: BuildOneTimeDiscountColumnsArgs): DataTableColumn<
    OneTimeDiscountListItem,
    OneTimeDiscountsSortBy
>[] {
    return [
        {
            key: 'ref',
            label: 'One-time discount',
            width: '8%',
            cellClassName: 'pl-6 pr-3 font-mono text-[13px] text-gray-500 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3 pl-6',
            render: (discount) => `#${shortRef(discount.id)}`
        },
        {
            key: 'amount',
            label: 'Amount',
            width: '7%',
            align: 'right',
            cellClassName: 'px-3 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            render: (discount) => (
                <span className="text-sm font-semibold text-gray-900 tabular-nums">
                    {formatMoneyDollars(discount.amount)}
                </span>
            )
        },
        {
            key: 'reason',
            label: 'Reason',
            width: '16%',
            truncate: true,
            cellClassName: 'px-3 text-sm text-gray-700',
            headerClassName: 'whitespace-nowrap px-3',
            getTitle: (discount) => discount.description,
            render: (discount) => discount.description
        },
        {
            key: 'practice',
            label: 'Practice',
            width: '12%',
            truncate: true,
            cellClassName: 'px-3 text-sm',
            headerClassName: 'whitespace-nowrap px-3',
            getTitle: (discount) => discount.practiceName ?? undefined,
            // Same name-over-doctor pairing as the Charges practice column.
            render: (discount) =>
                discount.practiceName ? (
                    <>
                        <span
                            className="block text-sm font-medium text-gray-800 truncate"
                            title={discount.practiceName}
                        >
                            {discount.practiceName}
                        </span>
                        {discount.practiceDoctorName && (
                            <span
                                className="block text-xs text-gray-400 truncate"
                                title={`Dr. ${discount.practiceDoctorName}`}
                            >
                                Dr. {discount.practiceDoctorName}
                            </span>
                        )}
                    </>
                ) : (
                    <span className="text-gray-300">-</span>
                )
        },
        {
            key: 'patient',
            label: 'Patient',
            width: '12%',
            sortable: true,
            sortKey: 'patient',
            truncate: true,
            cellClassName: 'px-3 text-sm',
            headerClassName: 'whitespace-nowrap px-3',
            getTitle: (discount) => discount.patientName ?? undefined,
            // Whose credit it is: the source charge's patient, else the applied charge's.
            render: (discount) =>
                discount.patientName ? (
                    <span className="text-gray-700">{discount.patientName}</span>
                ) : (
                    <span className="text-gray-300">-</span>
                )
        },
        {
            key: 'sourceCharge',
            label: 'Source charge',
            width: '8%',
            cellClassName: 'px-3 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            stopClickPropagation: true,
            render: (discount) =>
                discount.sourceChargeId ? (
                    <button
                        type="button"
                        onClick={() => onOpenCharge(discount.sourceChargeId as string)}
                        title={discount.sourceChargeDescription ?? undefined}
                        className="font-mono text-[12px] text-gray-600 underline decoration-gray-300 underline-offset-2 hover:text-gray-900"
                    >
                        #{shortRef(discount.sourceChargeId)}
                    </button>
                ) : (
                    <span className="text-sm text-gray-300">manual</span>
                )
        },
        {
            key: 'usedBy',
            label: 'Used by',
            width: '8%',
            cellClassName: 'px-3 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            stopClickPropagation: true,
            render: (discount) =>
                discount.chargeId ? (
                    <button
                        type="button"
                        onClick={() => onOpenCharge(discount.chargeId as string)}
                        className="font-mono text-[12px] text-gray-600 underline decoration-gray-300 underline-offset-2 hover:text-gray-900"
                    >
                        #{shortRef(discount.chargeId)}
                    </button>
                ) : (
                    <span className="text-sm text-gray-300">-</span>
                )
        },
        {
            key: 'expires',
            label: 'Expires',
            width: '8%',
            sortable: true,
            sortKey: 'expiry',
            cellClassName: 'px-3 text-sm whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            render: (discount) =>
                discount.expiresAt ? (
                    <span
                        className={
                            discount.status === 'expiring'
                                ? 'text-yellow-700 font-medium'
                                : 'text-gray-500'
                        }
                    >
                        {fmtDate(discount.expiresAt)}
                    </span>
                ) : (
                    <span className="text-gray-300">never</span>
                )
        },
        {
            key: 'status',
            label: 'Status',
            width: '8%',
            cellClassName: 'px-3',
            headerClassName: 'whitespace-nowrap px-3',
            render: (discount) => <StatusBadge status={discount.status} />
        },
        {
            key: 'created',
            label: 'Created',
            width: '8%',
            sortable: true,
            sortKey: 'date',
            cellClassName: 'px-3 text-sm text-gray-500 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            render: (discount) => fmtDate(discount.createdAt)
        },
        {
            key: 'menu',
            label: '',
            width: '5%',
            stopClickPropagation: true,
            cellClassName: 'px-3 w-12',
            headerClassName: 'whitespace-nowrap px-3',
            render: (discount) => {
                const items = menuItemsFor(
                    discount,
                    canEdit,
                    onLink,
                    onDelete,
                    onOpenCharge,
                    onUnlink
                );
                return items.length > 0 ? <RowMenu items={items} /> : null;
            }
        }
    ];
}
